import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/ui/search-input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useIBGEStates, useIBGECities } from "@/hooks/useIBGELocations";
import { fetchAddress } from "@/lib/cep";
import { cn } from "@/lib/utils";
import {
  PanelFieldLabel,
  PanelSectionHeading,
  panelBody,
  panelControl,
  panelHint,
  panelPage,
  panelSurface,
  panelTitle,
} from "@/components/producer/panel";

export function VenueStep({ form, updateField }: { form: any; updateField: (f: string, v: any) => void }) {
  const { t } = useTranslation("producerEventForm");
  const { states } = useIBGEStates();
  const { cities, loading: citiesLoading } = useIBGECities(form.venue_state);
  const [citySearch, setCitySearch] = useState("");
  const [cepLoading, setCepLoading] = useState(false);

  const filteredCities = citySearch
    ? cities.filter((c) => c.nome.toLowerCase().includes(citySearch.toLowerCase()))
    : cities;

  const handleCEPBlur = async () => {
    const cep = form.venue_zip?.replace(/\D/g, "");
    if (cep?.length === 8) {
      setCepLoading(true);
      try {
        const addr = await fetchAddress(cep);
        if (addr) {
          updateField("venue_address", addr.street || "");
          updateField("venue_neighborhood", addr.neighborhood || "");
          updateField("venue_city", addr.city || "");
          updateField("venue_state", addr.state || "");
        } else {
          toast({ title: t("form.venue.cepNotFound"), description: t("form.venue.cepNotFoundDesc"), variant: "destructive" });
        }
      } catch (err: any) {
        toast({ title: t("form.venue.cepError"), description: err?.message || t("form.venue.tryAgain"), variant: "destructive" });
      }
      setCepLoading(false);
    }
  };

  if (form.is_online) {
    return (
      <div className={panelPage}>
        <header>
          <h1 className={panelTitle}>{t("form.steps.venue.label")}</h1>
        </header>

        <div className={panelSurface}>
          <PanelSectionHeading>{t("form.venue.onlineTitle")}</PanelSectionHeading>
          <div className={panelBody}>
            <div>
              <PanelFieldLabel>{t("form.venue.onlineUrl")}</PanelFieldLabel>
              <Input
                value={form.online_url}
                onChange={(e) => updateField("online_url", e.target.value)}
                placeholder="https://meet.google.com/... ou https://zoom.us/..."
                disabled={form.configure_online_url_later}
                className={panelControl}
              />
              <p className={panelHint}>{t("form.venue.onlineUrlHint")}</p>
            </div>
            <label
              htmlFor="configure_later"
              className="flex cursor-pointer items-center gap-3 rounded-2xl bg-secondary/50 px-4 py-3.5 transition-colors duration-150 ease-out hover:bg-secondary/70"
            >
              <Checkbox
                id="configure_later"
                checked={form.configure_online_url_later}
                onCheckedChange={(checked) => {
                  updateField("configure_online_url_later", !!checked);
                  if (checked) {
                    updateField("online_url", "");
                  }
                }}
              />
              <span className="text-sm text-muted-foreground">{t("form.venue.configureLater")}</span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={panelPage}>
      <header>
        <h1 className={panelTitle}>{t("form.steps.venue.label")}</h1>
      </header>

      <div className={panelSurface}>
        <PanelSectionHeading>{t("form.venue.physicalTitle")}</PanelSectionHeading>
        <div className={panelBody}>
          <div>
            <PanelFieldLabel>{t("form.venue.venueName")}</PanelFieldLabel>
            <Input
              value={form.venue_name}
              onChange={(e) => updateField("venue_name", e.target.value)}
              placeholder={t("form.venue.venueNamePlaceholder")}
              maxLength={100}
              className={panelControl}
            />
            <p className={cn(panelHint, "tabular-nums")}>
              {t("form.sections.info.charactersRemaining", { count: 100 - (form.venue_name?.length || 0) })}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div>
              <PanelFieldLabel>{t("form.venue.zip")}</PanelFieldLabel>
              <Input
                value={form.venue_zip}
                onChange={(e) => updateField("venue_zip", e.target.value)}
                onBlur={handleCEPBlur}
                placeholder="00000-000"
                maxLength={9}
                className={panelControl}
              />
              {cepLoading && <p className={panelHint}>{t("form.venue.searching")}</p>}
            </div>
            <div className="sm:col-span-2">
              <PanelFieldLabel>{t("form.venue.address")}</PanelFieldLabel>
              <Input
                value={form.venue_address}
                onChange={(e) => updateField("venue_address", e.target.value)}
                placeholder={t("form.venue.addressPlaceholder")}
                className={panelControl}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <PanelFieldLabel>{t("form.venue.number")}</PanelFieldLabel>
              <Input
                value={form.venue_number}
                onChange={(e) => updateField("venue_number", e.target.value)}
                placeholder="123"
                className={panelControl}
              />
            </div>
            <div>
              <PanelFieldLabel>{t("form.venue.complement")}</PanelFieldLabel>
              <Input
                value={form.venue_complement}
                onChange={(e) => updateField("venue_complement", e.target.value)}
                placeholder={t("form.venue.complementPlaceholder")}
                maxLength={250}
                className={panelControl}
              />
            </div>
          </div>

          <div>
            <PanelFieldLabel>{t("form.venue.neighborhood")}</PanelFieldLabel>
            <Input
              value={form.venue_neighborhood}
              onChange={(e) => updateField("venue_neighborhood", e.target.value)}
              placeholder={t("form.venue.neighborhoodPlaceholder")}
              className={panelControl}
            />
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <PanelFieldLabel>{t("form.venue.state")}</PanelFieldLabel>
              <Select
                value={form.venue_state}
                onValueChange={(v) => {
                  updateField("venue_state", v);
                  updateField("venue_city", "");
                  setCitySearch("");
                }}
              >
                <SelectTrigger className={panelControl}>
                  <SelectValue placeholder={t("form.venue.select")} />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {states.map((s) => (
                    <SelectItem key={s.sigla} value={s.sigla}>{s.nome} ({s.sigla})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <PanelFieldLabel>{t("form.venue.city")}</PanelFieldLabel>
              {citiesLoading ? (
                <Input disabled placeholder={t("form.venue.loading")} className={panelControl} />
              ) : form.venue_state ? (
                <Select value={form.venue_city} onValueChange={(v) => updateField("venue_city", v)}>
                  <SelectTrigger className={panelControl}>
                    <SelectValue placeholder={t("form.venue.selectCity")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    <div className="px-2 pb-2">
                      <SearchInput
                        placeholder={t("form.venue.searchCity")}
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                        className="h-9 rounded-xl text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredCities.slice(0, 100).map((c) => (
                      <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
                    ))}
                    {filteredCities.length > 100 && (
                      <p className="px-2 py-1 text-xs text-muted-foreground">{t("form.venue.typeToFilter")}</p>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <Input disabled placeholder={t("form.venue.selectStateFirst")} className={panelControl} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
